import React, { useState } from "react";
import api from "../api";
import { FaLeaf, FaUniversity, FaPercent, FaHome } from "react-icons/fa";

function PropertyDetails({ data, onNewSearch }) {
  const [editable, setEditable] = useState({
    built_date: data.built_date?.exact_year || data.built_date || "",
    energy_eff: data.energy_eff,
    floor_area: data.floor_area,
    num_rooms: data.num_rooms,
    floor_level: data.floor_level || "",
  });

  const handleChange = (field, value) => {
    setEditable((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateReport = async () => {
    try {
      const payload = {
        full_address: data.address,
        city: data.city || "LONDON",
        postcode: data.postcode,
        built_date: String(editable.built_date),
        energy_eff: Number(editable.energy_eff),
        tenure: data.tenure,
        property_type: data.property_type,
        built_form: data.built_form,
        floor_area: parseFloat(editable.floor_area),
        floor_level: parseFloat(editable.floor_level),
        num_rooms: Number(editable.num_rooms),
        dist_to_park: parseFloat(data.dist_to_park),
        dist_to_tube: parseFloat(data.dist_to_tube),
        dist_to_school: parseFloat(data.dist_to_school),
      };

      const response = await api.post("/explain", payload);

      if (response.data?.report_url) {
        window.open(response.data.report_url, "_blank");
      } else {
        alert("No report URL returned by backend.");
      }
    } catch (err) {
      console.error("Error generating report:", err);
      alert("Failed to generate report.");
    }
  };

  return (
    <div className="mt-10 max-w-5xl w-full bg-white p-8 rounded-2xl shadow-lg text-left">
      <h2 className="text-2xl font-bold text-sky-900 mb-6">
        Property Characteristics
      </h2>

      {/* Editable + Read-only fields in grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Address (locked, full width) */}
        <div className="md:col-span-2">
          <label className="block text-gray-600 text-sm font-medium">
            Address
          </label>
          <input
            type="text"
            value={data.address}
            readOnly
            className="mt-1 w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-700"
          />
        </div>

        {/* Year Built */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Year Built
          </label>
          <input
            type="number"
            value={editable.built_date}
            onChange={(e) => handleChange("built_date", e.target.value)}
            className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700"
          />
        </div>

        {/* Energy Efficiency */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Energy Efficiency (EPC score)
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={editable.energy_eff}
            onChange={(e) => handleChange("energy_eff", e.target.value)}
            className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700"
          />
        </div>

        {/* Floor Area */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Floor Area (m²)
          </label>
          <input
            type="number"
            value={editable.floor_area}
            onChange={(e) => handleChange("floor_area", e.target.value)}
            className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700"
          />
        </div>

        {/* Number of Rooms */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Number of Rooms
          </label>
          <input
            type="number"
            value={editable.num_rooms}
            onChange={(e) => handleChange("num_rooms", e.target.value)}
            className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700"
          />
        </div>

        {/* Floor Level */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Floor Level
          </label>
          <input
            type="text"
            value={editable.floor_level}
            onChange={(e) => handleChange("floor_level", e.target.value)}
            className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700"
          />
        </div>

        {/* Tenure (read-only) */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Tenure
          </label>
          <p className="mt-1 px-4 py-2 bg-gray-100 rounded-lg text-gray-700">
            {data.tenure}
          </p>
        </div>

        {/* Property Type (read-only) */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Property Type
          </label>
          <p className="mt-1 px-4 py-2 bg-gray-100 rounded-lg text-gray-700">
            {data.property_type}
          </p>
        </div>

        {/* Built Form (read-only) */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Built Form
          </label>
          <p className="mt-1 px-4 py-2 bg-gray-100 rounded-lg text-gray-700">
            {data.built_form}
          </p>
        </div>

        {/* Distances (read-only) */}
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Distance to Park (km)
          </label>
          <p className="mt-1 px-4 py-2 bg-gray-100 rounded-lg text-gray-700">
            {data.dist_to_park.toFixed(2)}
          </p>
        </div>
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Distance to Tube (km)
          </label>
          <p className="mt-1 px-4 py-2 bg-gray-100 rounded-lg text-gray-700">
            {data.dist_to_tube.toFixed(2)}
          </p>
        </div>
        <div>
          <label className="block text-gray-600 text-sm font-medium">
            Distance to School (km)
          </label>
          <p className="mt-1 px-4 py-2 bg-gray-100 rounded-lg text-gray-700">
            {data.dist_to_school.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Insights section */}
      <h3 className="text-xl font-semibold text-sky-900 mt-10 mb-4">
        Insights & Analysis
      </h3>
      {/* Action buttons */}
      <div className="flex justify-between mt-8">
        <button
          type="button"
          onClick={handleGenerateReport}
          className="px-6 py-2 bg-sky-800 text-white rounded-lg hover:bg-sky-900"
        >
          Generate Report
        </button>
        <button
          type="button"
          onClick={onNewSearch}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          New Search
        </button>
      </div>
    </div>
  );
}

export default PropertyDetails;
